// @ts-check
'use strict';

/**
 * The site plan's access lines: thick lines a measurer draws along an External
 * area's elevation, each with its access type, as the standard proposal's
 * Access Plan shows them (legend: Mechanical Access (EWP) red, Fixed Scaffold
 * green, Industrial Rope Access yellow). Stored on the visit area
 * (hig_visitarea.hig_accesslines), drawn on the iPad's plan and into
 * "Site plan (access).jpg". The iPad and the API both use these rules: the
 * shape check, the curve drawn through a line's points, and the fingerprint
 * that says whether the saved image still shows today's lines.
 * Design: apps/measure-to-quote/docs/CONDITION-REGION.md "Access plan". Pure.
 */

/** @typedef {'ewp' | 'scaffold' | 'rope'} AccessTypeKey */
/** @typedef {{ key: AccessTypeKey, label: string, short: string, colour: string }} AccessType */
/** @typedef {[number, number]} PlanPoint */
/** @typedef {{ id: string, type: AccessTypeKey, points: PlanPoint[] }} AccessLine */
/** @typedef {{ c1: PlanPoint, c2: PlanPoint, to: PlanPoint }} CurveSegment */

/** The proposal's Access Plan legend, in its order: its names and colours. */
const ACCESS_TYPES = /** @type {ReadonlyArray<AccessType>} */ (
  Object.freeze([
    { key: 'ewp', label: 'Mechanical Access (EWP)', short: 'EWP', colour: '#ff0000' },
    { key: 'scaffold', label: 'Fixed Scaffold', short: 'Fixed scaffold', colour: '#00b050' },
    { key: 'rope', label: 'Industrial Rope Access', short: 'Rope access', colour: '#ffff00' },
  ])
);

/** The thin outline every line is drawn with, so it reads over light concrete and dark roofs alike (Higgins charcoal). */
const ACCESS_LINE_EDGE = '#252729';

/** hig_accesslines' shape version. */
const ACCESS_LINES_VERSION = 1;
/** Lines on one area, and points on one line: far more than a measurer draws, small enough for one column. */
const MAX_LINES = 50;
const MAX_POINTS = 400;
const LINE_ID = /^[A-Za-z0-9_-]{1,40}$/;

/** A line's width as a share of the plan's width, so it looks the same however large the plan is. */
const LINE_SHARE = 0.014;
/** The outline's width on each side, as a share of the line's width. */
const EDGE_SHARE = 0.2;
/** How far a simplified line may stray from the stroke, as a share of the line's width: well inside the line itself. */
const SIMPLIFY_SHARE = 0.25;

/** @param {unknown} key @returns {AccessType | null} */
const accessType = (key) => ACCESS_TYPES.find((t) => t.key === key) ?? null;

/** A line's width and outline (plan pixels) on a plan `planWidth` across. @param {number} planWidth */
function accessLineShape(planWidth) {
  const width = planWidth * LINE_SHARE;
  return { width, edge: width * EDGE_SHARE, simplify: width * SIMPLIFY_SHARE };
}

/** @param {PlanPoint} p @param {PlanPoint} a @param {PlanPoint} b distance from p to the segment a–b */
function distanceToSegment(p, a, b) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const length2 = dx * dx + dy * dy;
  const t = length2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / length2)) : 0;
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

/** How far a point is from a run of points (a line's, before smoothing). @param {PlanPoint} point @param {ReadonlyArray<PlanPoint>} points */
function distanceToPolyline(point, points) {
  if (points.length === 1) return Math.hypot(point[0] - points[0][0], point[1] - points[0][1]);
  let nearest = Infinity;
  for (let i = 0; i + 1 < points.length; i++) nearest = Math.min(nearest, distanceToSegment(point, points[i], points[i + 1]));
  return nearest;
}

/**
 * A finger's stroke with the jitter taken out (Ramer–Douglas–Peucker): the
 * fewest of its points that stay within `tolerance` of every point it had.
 * The ends are always kept.
 * @param {ReadonlyArray<PlanPoint>} points @param {number} tolerance @returns {PlanPoint[]}
 */
function simplifyStroke(points, tolerance) {
  if (points.length < 3) return points.map((p) => [p[0], p[1]]);
  const keep = new Array(points.length).fill(false);
  keep[0] = keep[points.length - 1] = true;
  /** @type {[number, number][]} */
  const spans = [[0, points.length - 1]];
  while (spans.length) {
    const [first, last] = /** @type {[number, number]} */ (spans.pop());
    let furthest = -1;
    let distance = tolerance;
    for (let i = first + 1; i < last; i++) {
      const d = distanceToSegment(points[i], points[first], points[last]);
      if (d > distance) [furthest, distance] = [i, d];
    }
    if (furthest < 0) continue;
    keep[furthest] = true;
    spans.push([first, furthest], [furthest, last]);
  }
  return points.filter((_, i) => keep[i]).map((p) => [p[0], p[1]]);
}

/** Centripetal Catmull-Rom (alpha 0.5): a curve through every point that never loops or overshoots on uneven spacing. */
const ALPHA = 0.5;

/** @param {PlanPoint} a @param {PlanPoint} b */
const knot = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]) ** ALPHA;

/**
 * One Catmull-Rom span (p1 to p2) as a cubic Bézier's two control points.
 * @param {PlanPoint} p0 @param {PlanPoint} p1 @param {PlanPoint} p2 @param {PlanPoint} p3 @returns {[PlanPoint, PlanPoint]}
 */
function bezierControls(p0, p1, p2, p3) {
  const d1 = knot(p0, p1);
  const d2 = knot(p1, p2);
  const d3 = knot(p2, p3);
  /** @param {number} i */
  const c1 = (i) => (d1 ? (d1 * d1 * p2[i] - d2 * d2 * p0[i] + (2 * d1 * d1 + 3 * d1 * d2 + d2 * d2) * p1[i]) / (3 * d1 * (d1 + d2)) : p1[i]);
  /** @param {number} i */
  const c2 = (i) => (d3 ? (d3 * d3 * p1[i] - d2 * d2 * p3[i] + (2 * d3 * d3 + 3 * d3 * d2 + d2 * d2) * p2[i]) / (3 * d3 * (d3 + d2)) : p2[i]);
  return [
    [c1(0), c1(1)],
    [c2(0), c2(1)],
  ];
}

/**
 * The smooth curve through a line's points, as cubic Bézier segments from the
 * first point: the same curve on screen (SVG) and in the saved image (canvas).
 * Two points make a straight segment.
 * @param {ReadonlyArray<PlanPoint>} points @returns {CurveSegment[]}
 */
function smoothCurve(points) {
  /** @type {CurveSegment[]} */
  const segments = [];
  for (let i = 0; i + 1 < points.length; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    const [c1, c2] = bezierControls(points[i - 1] ?? p1, p1, p2, points[i + 2] ?? p2);
    segments.push({ c1, c2, to: [p2[0], p2[1]] });
  }
  return segments;
}

/** @param {unknown} v @returns {v is number} */
const isFraction = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1;

/**
 * What's wrong with a hig_accesslines document, or null when it's sound:
 * { schemaVersion: 1, lines: [{ id, type, points: [[x, y], …] }] }, x and y 0–1 across and down the plan.
 * @param {unknown} doc @returns {string | null}
 */
function accessLinesProblem(doc) {
  if (typeof doc !== 'object' || doc === null || Array.isArray(doc)) return 'must be an object';
  const d = /** @type {Record<string, unknown>} */ (doc);
  const extra = Object.keys(d).find((k) => k !== 'schemaVersion' && k !== 'lines');
  if (extra) return `${extra} is not part of v${ACCESS_LINES_VERSION}`;
  if (d.schemaVersion !== ACCESS_LINES_VERSION) return `schemaVersion must be ${ACCESS_LINES_VERSION}`;
  if (!Array.isArray(d.lines) || d.lines.length > MAX_LINES) return `lines must be a list of up to ${MAX_LINES}`;
  for (const [i, line] of d.lines.entries()) {
    if (typeof line !== 'object' || line === null) return `lines[${i}] must be an object`;
    const l = /** @type {Record<string, unknown>} */ (line);
    const lineExtra = Object.keys(l).find((k) => !['id', 'type', 'points'].includes(k));
    if (lineExtra) return `lines[${i}].${lineExtra} is not part of v${ACCESS_LINES_VERSION}`;
    if (typeof l.id !== 'string' || !LINE_ID.test(l.id)) return `lines[${i}].id must be a short key`;
    if (!accessType(l.type)) return `lines[${i}].type must be one of ${ACCESS_TYPES.map((t) => t.key).join(', ')}`;
    if (!Array.isArray(l.points) || l.points.length < 2 || l.points.length > MAX_POINTS) return `lines[${i}].points must be 2 to ${MAX_POINTS} points`;
    if (l.points.some((p) => !Array.isArray(p) || p.length !== 2 || !isFraction(p[0]) || !isFraction(p[1]))) return `lines[${i}].points must each be [x, y], 0–1 across and down the plan`;
  }
  return null;
}

/** A visit area's lines from its stored column; none when blank or unreadable. @param {unknown} raw @returns {AccessLine[]} */
function parseAccessLines(raw) {
  if (typeof raw !== 'string' || !raw) return [];
  try {
    const doc = JSON.parse(raw);
    return accessLinesProblem(doc) === null ? doc.lines : [];
  } catch {
    return [];
  }
}

/** The column's text for a list of lines. @param {ReadonlyArray<AccessLine>} lines */
const accessLinesJson = (lines) => JSON.stringify({ schemaVersion: ACCESS_LINES_VERSION, lines });

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/** A short fingerprint (FNV-1a, 8 hex digits) of a text: the same on every device and the server. @param {string} text */
function fingerprint(text) {
  let hash = FNV_OFFSET;
  for (const ch of text) hash = Math.imul(hash ^ ch.charCodeAt(0), FNV_PRIME) >>> 0;
  return hash.toString(16).padStart(8, '0');
}

/** Which plan a hig_siteplan document is: a remade plan replaces the same file, so its savedOn tells them apart. @param {{ driveItemId: string, savedOn?: string }} plan */
const planKeyOf = (plan) => `${plan.driveItemId}|${plan.savedOn ?? ''}`;

/**
 * The fingerprint of a plan's access lines: External areas only, in id order,
 * so a device and the server reading the same lines agree. Recorded on the
 * visit (hig_siteplan.access.lines) when "Site plan (access).jpg" is saved.
 * @param {string} planKey @param {ReadonlyArray<{ id: string, side?: unknown, accessLines?: unknown }>} visitAreas
 */
function accessFingerprint(planKey, visitAreas) {
  const drawn = visitAreas
    .filter((a) => a.side === 'External')
    .map((a) => [a.id.toLowerCase(), parseAccessLines(a.accessLines)])
    .filter(([, lines]) => lines.length)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return fingerprint(JSON.stringify([planKey, drawn]));
}

module.exports = {
  ACCESS_TYPES,
  ACCESS_LINE_EDGE,
  ACCESS_LINES_VERSION,
  accessType,
  accessLineShape,
  simplifyStroke,
  distanceToPolyline,
  smoothCurve,
  accessLinesProblem,
  parseAccessLines,
  accessLinesJson,
  fingerprint,
  planKeyOf,
  accessFingerprint,
};
